// src/utils/instructorCategory.js — Pass 46E: ONE canonical resolver for an
// instructor's SAS category. Fixes the silent "missing → SAS School" corruption.
//
// TWO legacy field systems exist in the data:
//   · instructorPool      : 'internal' | 'collective'      (production import)
//   · instructorCategory  : 'sas_school' | 'sas_collective' (demo/historical recovery)
// Neither must ever silently become School when absent. Category is also NOT a
// timeless person attribute — a person's SAS relationship can change per season
// (e.g. Euan: 24/25 School, 25/26 Collective), so we resolve CURRENT vs a specific
// SEASON, backed by an additive `seasonMemberships` map.

export const CATEGORY = { SCHOOL: 'school', COLLECTIVE: 'collective', UNCLASSIFIED: 'unclassified' };
export const CATEGORY_LABEL = { school: 'SAS School', collective: 'Collective', unclassified: 'Needs review' };

// Map either legacy field to the canonical category (null if unrecognised — NOT school).
// Pass 57B (items 2/3) — the ops "set School/Collective for this season" patch. Writes the affiliation
// onto the SEASON relationship (seasonMemberships[season].category), NOT the global human identity, so
// the same person can be School at org/season A and Collective elsewhere. Pure — caller applies via a
// gated writer on the org-scoped member doc. category ∈ 'school'|'collective'.
export function setSeasonCategoryPatch(member = {}, category, seasonId = CURRENT_SEASON_ID) {
  const legacy = category === CATEGORY.SCHOOL ? 'sas_school' : category === CATEGORY.COLLECTIVE ? 'sas_collective' : null;
  if (!legacy) throw new Error(`setSeasonCategoryPatch: category must be school|collective, got ${category}`);
  const sm = (member.seasonMemberships && typeof member.seasonMemberships === 'object') ? member.seasonMemberships : {};
  return { seasonMemberships: { ...sm, [seasonId]: { ...(sm[seasonId] || {}), category: legacy } } };
}

export function poolToCategory(pool) {
  if (pool === 'internal') return CATEGORY.SCHOOL;
  if (pool === 'collective') return CATEGORY.COLLECTIVE;
  return null;
}
export function legacyCategoryToCategory(c) {
  if (c === 'sas_school') return CATEGORY.SCHOOL;
  if (c === 'sas_collective') return CATEGORY.COLLECTIVE;
  if (c === 'school' || c === 'collective') return c;
  return null;
}

// Latest season on record (seasonMemberships keys sort lexically: 2024_25 < 2025_26 < 2026_27).
export function latestSeasonCategory(member = {}) {
  const sm = member.seasonMemberships;
  if (!sm || typeof sm !== 'object') return null;
  const keys = Object.keys(sm).filter((k) => sm[k] && sm[k].category).sort();
  if (!keys.length) return null;
  return legacyCategoryToCategory(sm[keys[keys.length - 1]].category);
}

// Resolve a member's category.
//   opts.season → return that season's affiliation (from seasonMemberships) if present.
//   otherwise CURRENT: explicit pool/category field → else latest known season → else UNCLASSIFIED.
// NEVER defaults an unknown to School.
export function resolveInstructorCategory(member = {}, opts = {}) {
  const { season } = opts;
  if (season) {
    const s = member.seasonMemberships && member.seasonMemberships[season];
    if (s && s.category) return legacyCategoryToCategory(s.category) || CATEGORY.UNCLASSIFIED;
    // no explicit record for that season → unclassified for THAT season (do not borrow current)
    return CATEGORY.UNCLASSIFIED;
  }
  return poolToCategory(member.instructorPool)
    || legacyCategoryToCategory(member.instructorCategory)
    || latestSeasonCategory(member)
    || CATEGORY.UNCLASSIFIED;
}

// Pass 47: CURRENT-TEAM STATE — a member's relationship to the CURRENT operating
// season is distinct from "has any membership". A 25/26 member is NOT automatically
// current for 26/27. Positive proof = explicit current-season membership or an
// explicit currentTeam:true override. Absent evidence = needs review, NEVER current.
export const TEAM_STATE = { CURRENT: 'confirmed_current', REVIEW: 'needs_roster_review', PREVIOUS: 'previous_staff' };
export const CURRENT_SEASON_ID = '2026_27';
export function resolveCurrentTeamState(member = {}, currentSeason = CURRENT_SEASON_ID) {
  const sm = member.seasonMemberships && member.seasonMemberships[currentSeason];
  // Pass 48 §10: participation is SEPARATE from commercial category. A member confirmed
  // current for the season is CURRENT even if their School/Collective category is unresolved
  // (e.g. Jed = driving/concierge, unclassified). Explicit "not_returning" → previous.
  // Precedence (Pass 49A §2): EXPLICIT current-season participation FULLY WINS. Once it
  // exists we never infer from category/currentTeam — 'unknown' means unknown (REVIEW),
  // not "guess from last season". category/currentTeam are legacy compatibility used ONLY
  // when participation is ABSENT.
  if (sm && sm.participation) {
    if (sm.participation === 'current') return TEAM_STATE.CURRENT;
    if (sm.participation === 'not_returning') return TEAM_STATE.PREVIOUS;
    return TEAM_STATE.REVIEW; // 'unknown' (or any other explicit value) → needs review
  }
  // ── participation ABSENT → legacy compatibility ──
  if (sm && sm.category) return TEAM_STATE.CURRENT;             // legacy: category implies current
  if (member.currentTeam === false) return TEAM_STATE.PREVIOUS; // explicitly not current
  const hasHistory = member.seasonMemberships && Object.keys(member.seasonMemberships).length > 0;
  if (member.currentTeam === true && !hasHistory) return TEAM_STATE.CURRENT; // legacy-only
  return TEAM_STATE.REVIEW;                                     // absent/other = needs review
}

// Pass 49A §10: LEGACY heuristic, isolated + named. A canonical staff identity is not a
// Firebase Auth UID. Until every member is Auth-linked, an unlinked canonical id must not
// receive a notification addressed to it. Prefer explicit Auth linkage over this heuristic;
// it exists only so a genuinely legacy instructorId (that WAS an Auth uid) still works.
export function isCanonicalMemberId(id = '') {
  return /^seed_instructor_/.test(String(id || ''));
}

// Pass 49A §11: "operational SAS person" — appears in Team/Schedule based on work
// evidence, NOT on being a teaching instructor. Auth/workspace role stays separate.
export function isOperationalStaff(member = {}) {
  return member.role === 'instructor' || member.orgRole === 'instructor'
    || (member.seasonMemberships && Object.keys(member.seasonMemberships).length > 0)
    || !!member.jobFunction || member.isTeachingInstructor !== undefined
    || (Array.isArray(member.workCapabilities) && member.workCapabilities.length > 0);
}

// Pass 58G1 — STRICT current-SAS-team classifiers for CURRENT production summaries (Overview/Team).
// Unlike the permissive splitTeachingStaff() ("teaching unless isTeachingInstructor===false"), these use
// POSITIVE current evidence and must NOT count John (owner, non-teaching), operations-only staff,
// disconnected Collective profiles, or review/historical members. splitTeachingStaff is unchanged for
// historical/back-compat callers.
// A current SAS TEACHING member = confirmed-current + SAS School relationship this season + not explicitly
// non-teaching. (Source instructors whose isTeachingInstructor is unset but who are SAS School still teach.)
export function isCurrentSasTeachingMember(member = {}, season = CURRENT_SEASON_ID) {
  return resolveCurrentTeamState(member, season) === TEAM_STATE.CURRENT
    && resolveInstructorCategory(member, { season }) === CATEGORY.SCHOOL
    && member.isTeachingInstructor !== false;
}
// A current SAS OPERATIONS (non-teaching) member = confirmed-current, NOT a SAS teaching member, and NOT a
// disconnected Collective profile (Collective is a separate, unconnected source). Owner (John) + drivers count.
export function isCurrentSasOperationsMember(member = {}, season = CURRENT_SEASON_ID) {
  return resolveCurrentTeamState(member, season) === TEAM_STATE.CURRENT
    && !isCurrentSasTeachingMember(member, season)
    && resolveInstructorCategory(member, { season }) !== CATEGORY.COLLECTIVE;
}

// Pass 81A — ELIGIBILITY for an operational source is teaching + current, INDEPENDENT of commercial category. A source
// RELATIONSHIP (which source+season a member participates in) is a SEPARATE, explicit axis (config/sourceRelationships)
// — category is NOT the authority for "may this member appear in operational source X?". This category-agnostic
// predicate is the operational-eligibility half; the explicit source-relationship seam is the other half. It lets ONE
// canonical human be eligible supply for MULTIPLE sources (SAS School AND Collective) without a category rewrite (§8).
export function isCurrentTeachingMember(member = {}, season = CURRENT_SEASON_ID) {
  return resolveCurrentTeamState(member, season) === TEAM_STATE.CURRENT
    && member.isTeachingInstructor !== false;
}

// Pass 81 — a current SAS COLLECTIVE teaching member = confirmed-current + a COLLECTIVE relationship this season +
// not explicitly non-teaching. This is the authoritative-SUPPLY predicate for the SEPARATE Collective operational
// source: it GATES which resolved source-instructor labels may create a Collective hold — it is NOT a supply
// enumeration (supply is derived from actual Collective source commitments, §16). It is the exact mirror of
// isCurrentSasTeachingMember with CATEGORY.COLLECTIVE, so a SAS-School member can never receive a Collective hold and
// a Collective member can never receive a SAS-School hold — the two source authorities never bleed (§8/§16/§22).
export function isCurrentCollectiveTeachingMember(member = {}, season = CURRENT_SEASON_ID) {
  return resolveCurrentTeamState(member, season) === TEAM_STATE.CURRENT
    && resolveInstructorCategory(member, { season }) === CATEGORY.COLLECTIVE
    && member.isTeachingInstructor !== false;
}

// Pass 81E — SAS OPERATIONAL-GRID ASSIGNMENT eligibility, a SEPARATE axis from SAS-School SUPPLY. The SAS instructor
// grid is intentionally a BLENDED operational view: Sam deliberately places Collective instructors who are FULFILLING
// SAS bookings into it (the "BZ onwards" section) so one sheet shows everyone covering SAS work. So "may this canonical
// human appear as valid SAS operational-allocation evidence on the authoritative grid?" is NOT the same question as
// "is this human SAS-School supply?". This predicate answers the FIRST: current + teaching, CATEGORY-AGNOSTIC — a
// resolved current teaching human (School OR Collective) placed on the authoritative SAS grid may create SAS allocation
// evidence + block availability + be assigned to an SAS booking. It does NOT convert them into SAS-School roster / team
// count / supply / category (§2/§6/§8). It is NOT a broad "any member can teach": it requires confirmed-current +
// teaching-capable, and the caller still requires an EXACT canonical resolution first (unknown/ambiguous fail closed,
// §10). Grid COLUMN POSITION is never the identity authority — canonical resolution is (§5). Distinct from
// isCurrentSasTeachingMember so a future change to SAS-School supply meaning never silently widens grid assignment.
export function isCurrentSasGridEligible(member = {}, season = CURRENT_SEASON_ID) {
  return resolveCurrentTeamState(member, season) === TEAM_STATE.CURRENT
    && member.isTeachingInstructor !== false;
}

export const isCollectiveMember = (member) => resolveInstructorCategory(member) === CATEGORY.COLLECTIVE;
export const isSchoolMember = (member) => resolveInstructorCategory(member) === CATEGORY.SCHOOL;
export const isUnclassifiedMember = (member) => resolveInstructorCategory(member) === CATEGORY.UNCLASSIFIED;

// Pass 49B §6/§10/§11: COMMERCIAL category and roster PARTICIPATION are SEPARATE axes and must
// both stay visible. A person can be commercial-category School AND participation unknown.
//   staffCategoryLabel  → commercial category only ('SAS School' | 'Collective' | 'Category not set')
//   staffParticipationLabel → roster participation only ('Current' | 'Needs roster review' | 'Previous')
//   staffStatusLabel    → the compact combined label for a row
// Pass 82C.1 — optional `labels` map ({school, collective}) makes the category WORDING organization-aware. Defaults to
// the SAS CATEGORY_LABEL so every existing caller is byte-for-byte; a tenant surface passes orgCategoryLabels(org).
export function staffCategoryLabel(member = {}, season, labels = CATEGORY_LABEL) {
  const cat = resolveInstructorCategory(member, season ? { season } : {});
  if (cat === CATEGORY.SCHOOL) return labels.school || CATEGORY_LABEL.school;
  if (cat === CATEGORY.COLLECTIVE) return labels.collective || CATEGORY_LABEL.collective;
  return 'Category not set';                                      // NOT "roster review"
}

export function staffParticipationLabel(member = {}, season) {
  const st = resolveCurrentTeamState(member, season || CURRENT_SEASON_ID);
  if (st === TEAM_STATE.REVIEW) return 'Needs roster review';
  if (st === TEAM_STATE.PREVIOUS) return 'Previous';
  return 'Current';
}

// Compact "participation · category" — but for the unremarkable confirmed-current + set
// category case, just show the category (e.g. "SAS School"). Review/Previous or a missing
// category always surface the participation prefix so BOTH facts stay visible.
export function staffStatusLabel(member = {}, season, labels = CATEGORY_LABEL) {
  const st = resolveCurrentTeamState(member, season || CURRENT_SEASON_ID);
  // Pass 58G1 — a NON-TEACHING operations person (owner/driver/ops) does NOT need a SAS School/Collective
  // commercial category. Show a human role/status, never "Category not set", on the daily schedule.
  if (member.isTeachingInstructor === false || member.jobFunction) {
    const roleWord = member.orgRole === 'organization_owner' || member.role === 'organization_owner'
      ? 'Operations'
      : member.jobFunction
        ? `Operations · ${String(member.jobFunction).charAt(0).toUpperCase() + String(member.jobFunction).slice(1)}`
        : 'Operations';
    if (st === TEAM_STATE.PREVIOUS) return `Previous · ${roleWord}`;
    return roleWord;
  }
  const cat = staffCategoryLabel(member, season, labels);
  if (st === TEAM_STATE.REVIEW) return `Needs roster review · ${cat}`;
  if (st === TEAM_STATE.PREVIOUS) return `Previous · ${cat}`;
  return cat === 'Category not set' ? 'Current · Category not set' : cat;
}
